import { useCallback, useEffect, useRef, useState } from "react";

import { useMatrix } from "~/context/MatrixContext";
import { loadMembers, type RoomMember, type RoomSummary } from "~/lib/matrix";
import { dmEligibleForOrg, isJoinedMember, type OrgRoster } from "~/lib/org-dm-membership";

// At most MAX_CONCURRENT transport calls in flight at once; the limiter is
// shared across generations and never reset per-generation, so overlapping
// generations cannot each start four.
class ConcurrencyLimit {
  private running = 0;
  private waiters: (() => void)[] = [];

  async run<T>(fn: () => Promise<T>): Promise<T> {
    if (this.running < MAX_CONCURRENT) this.running += 1;
    else await new Promise<void>((resolve) => this.waiters.push(resolve));
    try {
      return await fn();
    } finally {
      this.running -= 1;
      const next = this.waiters.shift();
      if (next) {
        this.running += 1;
        next();
      }
    }
  }
}

const MAX_CONCURRENT = 4;

export interface UseOrgDmMembershipResult {
  /** No org is selected ("All"); every DM stays visible, no load runs. */
  all: boolean;
  /** The organisation roster the DM rows admit against. */
  orgRoster: OrgRoster;
  /** Per-DM admission for the currently scoped set; absent ⇒ excluded. */
  dmEligibleByRoomId: Map<string, boolean>;
  /** Whether a DM row is blocked because its own roster failed (excluded). */
  dmPendingByRoomId: Map<string, boolean>;
  /** Retry the failed loads. */
  retry: () => void;
}

export function useOrgDmMembership(
  orgSpaceId: string | null,
  rooms: RoomSummary[],
): UseOrgDmMembershipResult {
  const { client, userId } = useMatrix();
  const all = orgSpaceId === null || !client;

  // A new key supersedes every in-flight load: bump the generation and reset
  // the snapshot so a newer selection never inherits an older one.
  const generationRef = useRef(0);
  const limiterRef = useRef(new ConcurrencyLimit());
  const inflightRef = useRef(new Map<string, Promise<RoomMember[] | null>>());
  const memberCountRef = useRef(new Map<string, number>());
  const [nonce, setNonce] = useState(0);
  const [orgStatus, setOrgStatus] = useState<OrgRoster>({ status: "pending" });
  const [dmState, setDmState] = useState<{
    eligible: Map<string, boolean>;
    failed: Map<string, boolean>;
  }>({ eligible: new Map(), failed: new Map() });

  // Sign-out / account / org change supersedes every in-flight load and the
  // cached snapshot so a newer selection never inherits an older one.
  useEffect(() => {
    generationRef.current += 1;
    inflightRef.current.clear();
    memberCountRef.current.clear();
    setOrgStatus({ status: "pending" });
    setDmState({ eligible: new Map(), failed: new Map() });
  }, [userId, client, orgSpaceId]);

  // One coalesced, ≤4-concurrent load. A failed room resolves to null (that
  // row is excluded) rather than rejecting, so one bad DM never drops the
  // others; a success stays cached until a generation change.
  const loadOne = useCallback(
    (roomId: string): Promise<RoomMember[] | null> => {
      if (!client) return Promise.resolve(null);
      const prior = inflightRef.current.get(roomId);
      if (prior) return prior;
      const p = limiterRef.current.run(async () => {
        try {
          return await loadMembers(client, roomId);
        } catch {
          return null as RoomMember[] | null;
        }
      });
      inflightRef.current.set(roomId, p);
      // Evict a failed row so retry reloads it; keep successes cached until a
      // generation change.
      void p.then((result) => {
        if (result === null) inflightRef.current.delete(roomId);
      });
      return p;
    },
    [client],
  );

  useEffect(() => {
    if (all) return;
    const gen = generationRef.current;
    let cancelled = false;
    const isStale = () => cancelled || generationRef.current !== gen;

    const selfId = userId ?? "";
    const dmIds = rooms.filter((room) => room.isDirect && room.archived === null).map((r) => r.id);

    // A changed memberCount means real membership moved, so evict the cached
    // roster and refetch. A member-load re-emission keeps memberCount stable,
    // so this cannot chain into another load — no loop. Index counts once.
    const countById = new Map(rooms.map((room) => [room.id, room.memberCount]));
    const refreshIds = [...new Set([...dmIds, orgSpaceId!])];
    for (const id of refreshIds) {
      const count = countById.get(id) ?? -1;
      const prev = memberCountRef.current.get(id);
      if (prev !== undefined && count !== prev) inflightRef.current.delete(id);
      memberCountRef.current.set(id, count);
    }

    void loadOne(orgSpaceId!).then((orgMembers) => {
      if (isStale()) return;
      if (orgMembers === null) {
        setOrgStatus({ status: "error" });
        return;
      }
      const joinedUserIds = new Set<string>();
      for (const member of orgMembers) {
        if (isJoinedMember(member)) joinedUserIds.add(member.userId);
      }
      const roster: OrgRoster = { status: "ready", joinedUserIds };
      setOrgStatus(roster);

      // Each DM settles independently against the resolved roster: one stalled
      // or failed DM never hides the others.
      for (const id of dmIds) {
        void loadOne(id).then((members) => {
          if (isStale()) return;
          if (members === null) {
            setDmState((prev) => {
              const failed = new Map(prev.failed);
              failed.set(id, true);
              return { ...prev, failed };
            });
            return;
          }
          const eligible = dmEligibleForOrg(members, selfId, roster);
          setDmState((prev) => {
            const next = new Map(prev.eligible);
            next.set(id, eligible);
            return { ...prev, eligible: next };
          });
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [all, client, userId, orgSpaceId, rooms, nonce, loadOne]);

  const retry = useCallback(() => {
    setOrgStatus({ status: "pending" });
    setDmState({ eligible: new Map(), failed: new Map() });
    setNonce((n) => n + 1);
  }, []);

  return {
    all,
    orgRoster: orgStatus,
    dmEligibleByRoomId: dmState.eligible,
    dmPendingByRoomId: dmState.failed,
    retry,
  };
}
